import type { DeskPhoneLink, DeskPhoneView } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { del, errorMessage, get, patch, post } from "../../api";
import { Alert, Button, Field, Loading, Panel, PanelHeader, formatDateTime } from "../../components/ui";
import { useSession } from "../../session";

type Draft = { name: string; address: string; pushUser: string; pushPassword: string };
const blank: Draft = { name: "Front desk phone", address: "https://", pushUser: "", pushPassword: "" };

/**
 * Yealink desk phones that show who's calling: the phone's incoming-call Action URL asks Vaulted, and
 * Vaulted pushes a short screen (name, next and last visit, membership, flags) to the phone's forwarded port.
 */
export function DeskPhones() {
  const qc = useQueryClient();
  const { can } = useSession();
  const list = useQuery({ queryKey: ["desk-phones"], queryFn: () => get<DeskPhoneView[]>("/api/desk-phones"), enabled: can("settings.manage") });
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft } | null>(null);
  const [link, setLink] = useState<{ id: string; url: string } | null>(null);
  const [tested, setTested] = useState<Record<string, string>>({});
  const refresh = () => qc.invalidateQueries({ queryKey: ["desk-phones"] });
  const save = useMutation({
    mutationFn: async (e: { id: string | null; draft: Draft }) => {
      if (e.id) return void (await patch(`/api/desk-phones/${e.id}`, e.draft));
      const r = await post<DeskPhoneLink>("/api/desk-phones", e.draft);
      setLink({ id: r.phone.id, url: r.actionUrl });
    },
    onSuccess: () => (setEditing(null), void refresh()),
  });
  const newLink = useMutation({
    mutationFn: (id: string) => post<DeskPhoneLink>(`/api/desk-phones/${id}/new-link`, {}),
    onSuccess: (r) => (setLink({ id: r.phone.id, url: r.actionUrl }), void refresh()),
  });
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/desk-phones/${id}`), onSuccess: refresh });
  const test = useMutation({
    mutationFn: async (id: string) => ({ id, r: await post<{ ok: boolean; error: string | null }>(`/api/desk-phones/${id}/test`, {}) }),
    onSuccess: ({ id, r }) => (setTested((t) => ({ ...t, [id]: r.ok ? "Sent: check the phone's screen." : (r.error ?? "It didn't go through.") })), void refresh()),
  });
  if (!can("settings.manage")) return null;
  const set = (k: keyof Draft, v: string) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, [k]: v } } : e));
  const err = save.error ?? newLink.error ?? remove.error ?? test.error;
  return (
    <Panel>
      <PanelHeader
        title="Desk phone caller screen"
        description="A Yealink desk phone (like the T57W) shows who's calling: name and age, next and last visit, membership, credit or balance, and flags (check guardian, no card, forms not done). Numbers that aren't clients show their saved name, or “Not a client”. The phone's incoming-call Action URL asks Vaulted, and Vaulted sends the screen to the phone through the port the clinic forwards to it."
      />
      <div className="flex flex-col gap-3 px-5 py-5 text-sm">
        {list.isLoading ? (
          <Loading />
        ) : (
          (list.data ?? []).map((p) => (
            <div key={p.id} className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-line p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-muted">
                    {p.address}
                    {p.pushUser ? ` · push login ${p.pushUser}` : " · no push login"}
                    {p.lastCallAt ? ` · last call ${formatDateTime(p.lastCallAt)}` : " · no calls yet"}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" busy={test.isPending && test.variables === p.id} onClick={() => test.mutate(p.id)}>
                    Send a test screen
                  </Button>
                  <Button variant="quiet" onClick={() => setEditing({ id: p.id, draft: { name: p.name, address: p.address, pushUser: p.pushUser, pushPassword: "" } })}>
                    Change
                  </Button>
                  <Button variant="quiet" busy={newLink.isPending} onClick={() => window.confirm("Make a new Action URL? The phone's current one stops working until you paste in the new one.") && newLink.mutate(p.id)}>
                    New Action URL
                  </Button>
                  <Button variant="quiet" onClick={() => window.confirm(`Remove ${p.name}?`) && remove.mutate(p.id)}>
                    Remove
                  </Button>
                </div>
              </div>
              {link?.id === p.id && (
                <Alert tone="ok">
                  Paste this into the phone: Features → Action URL → Incoming Call. It's shown only now.
                  <code className="mt-1 block break-all rounded bg-white px-2 py-1 text-xs text-ink">{link.url}</code>
                </Alert>
              )}
              {tested[p.id] && <Alert tone={tested[p.id]!.startsWith("Sent") ? "ok" : "danger"}>{tested[p.id]}</Alert>}
              {p.lastError && !tested[p.id] && <Alert>Last push didn't go through: {p.lastError}</Alert>}
            </div>
          ))
        )}
        {editing ? (
          <form
            className="flex flex-col gap-3 rounded-[var(--radius-control)] border border-line p-3"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(editing);
            }}
          >
            <Field label="Name" value={editing.draft.name} onChange={(e) => set("name", e.target.value)} />
            <Field
              label="Phone's address"
              placeholder="https://203.0.113.10:8443"
              value={editing.draft.address}
              onChange={(e) => set("address", e.target.value)}
              hint="The clinic's public IP (or a name pointing at it) and the outside port forwarded to the phone's HTTPS port 443."
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Push username" value={editing.draft.pushUser} onChange={(e) => set("pushUser", e.target.value)} hint="Features → Remote Control on the phone." />
              <Field label="Push password" type="password" value={editing.draft.pushPassword} onChange={(e) => set("pushPassword", e.target.value)} hint={editing.id ? "Leave blank to keep the saved one." : undefined} />
            </div>
            <div className="flex gap-2">
              <Button type="submit" variant="primary" busy={save.isPending}>
                {editing.id ? "Save" : "Add phone"}
              </Button>
              <Button variant="quiet" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <Button variant="secondary" className="self-start" onClick={() => setEditing({ id: null, draft: blank })}>
            + Add a desk phone
          </Button>
        )}
        {err && <Alert>{errorMessage(err)}</Alert>}
      </div>
    </Panel>
  );
}
